import { useState, useContext, useEffect } from "react";

// @mui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

import toast from "react-hot-toast";
import PhoneInput from "react-phone-number-input";
import { isPossiblePhoneNumber, formatPhoneNumberIntl } from "react-phone-number-input";
import { useForm, Controller } from "react-hook-form";
import { countries as countryData } from "utils/countries";
import { usaStates } from "utils/data";

import { StoreContext } from "context/store";
import { AccountContext } from "context/account";

const countryOptions = countryData.map((c) => ({
  label: c.name,
  value: c.alpha2.toLocaleLowerCase(),
}));

const usaStateOptions = usaStates.map((x) => {
  return {
    label: x.abbreviation,
    value: x.abbreviation,
  };
});

const ContactInfo = ({ onNext, onPrev, isNextDisabled, isPrevDisabled, saveNext }) => {
  const [phone, setPhone] = useState();
  const [countryCode, setCountryCode] = useState({});
  const { store, updateStore } = useContext(StoreContext);
  const account = useContext(AccountContext);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    control,
  } = useForm();

  useEffect(() => {
    const country_code = store?.metadata?.address?.country_code;
    let province = store?.metadata?.address?.province;

    // reset business phone number
    setValue("business_phone", store?.metadata?.business_phone);
    setPhone(store?.metadata?.business_phone);

    // reset country code

    const record = countryData.find((x) => x.alpha2.toLowerCase() == country_code);
    const countryOption = {
      label: record?.name,
      value: record?.alpha2.toLowerCase(),
    };
    setCountryCode(countryOption);
    // reset state code
    if (country_code == "us") {
      const state = usaStateOptions.find((x) => x.value.toUpperCase() == province);

      province = state;
    }

    reset({
      email: account?.email,
      business_phone: store?.metadata?.business_phone,
      address_1: store?.metadata?.address?.address_1,
      address_2: store?.metadata?.address?.address_2,
      city: store?.metadata?.address?.city,
      province: province,
      country_code: countryOption,
      postal_code: store?.metadata?.address?.postal_code,
      instagram_link: store?.metadata?.instagram_link,
      company_link: store?.metadata?.company_link,
      facebook_link: store?.metadata?.facebook_link,
      twitter_link: store?.metadata?.twitter_link,
    });
  }, [store]);

  const handleChangePhoneNumber = (value) => {
    setValue("business_phone", value);
    setPhone(value);
  };

  const handleChangeCountry = (option) => {
    setCountryCode(option);

    setValue("country_code", option);
    let province = getValues("province");

    if (option.value != "us") {
      if (province && typeof province == "object") {
        setValue("province", province.value);
      }
    } else if (option.value == "us" && province) {
      const provinceOpt = usaStateOptions.find((x) => x.value == province);
      if (provinceOpt && provinceOpt.value) {
        setValue("province", provinceOpt);
      } else {
        setValue("province", undefined);
      }
    }
  };

  const handleSave = async (data) => {
    const phoneNum = formatPhoneNumberIntl(data["business_phone"]);
    if (!isPossiblePhoneNumber(phoneNum)) {
      return toast.error("Not a valid phone number!");
    }
    try {
      const payload = {
        metadata: {
          address: {
            address_1: data["address_1"],
            address_2: data["address_2"],
            province:
              typeof data["province"] == "object" ? data["province"].value : data["province"],
            city: data["city"],
            postal_code: data["postal_code"],
            country_code:
              typeof data["country_code"] == "object"
                ? data["country_code"].value
                : data["country_code"],
          },
          business_phone: data["business_phone"],
          instagram_link: data["instagram_link"],
          company_link: data["company_link"],
          facebook_link: data["facebook_link"],
          twitter_link: data["twitter_link"],
        },
      };
      await updateStore(payload);
      toast.success("Update company information successfully!");
      saveNext();
    } catch (err) {
      toast.error("Update company information failed!");
    }
  };

  const handlePhoneCountryChange = (country) => {
    const record = countryData.find((x) => x.alpha2 == country);
    const countryOption = {
      label: record?.name,
      value: record?.alpha2.toLowerCase(),
    };
    if (countryOption && countryOption.value) {
      handleChangeCountry(countryOption);
    }
  };

  return (
    <Card id="contact-info" sx={{ overflow: "visible" }} className="my-2">
      <SuiBox p={3}>
        <SuiTypography variant="h5">Contact Info</SuiTypography>
      </SuiBox>
      <SuiBox component="form" pb={3} px={3} onSubmit={handleSubmit(handleSave)}>
        <Grid container spacing={3} className="items-start">
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                >
                  Email
                </SuiTypography>
              </SuiBox>
              <SuiInput
                placeholder="Email"
                readOnly
                disabled
                {...register("email", { required: true })}
              />
            </SuiBox>
          </Grid>

          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  Phone
                </SuiTypography>
              </SuiBox>

              <PhoneInput
                label="Phone"
                className="upstyled-phone-input h-10 px-2 rounded-lg"
                placeholder="Enter phone number"
                name="business_phone"
                value={phone}
                defaultCountry="US"
                onCountryChange={handlePhoneCountryChange}
                {...register("business_phone", { required: true })}
                required
                onChange={handleChangePhoneNumber}
              />
            </SuiBox>
          </Grid>

          <Grid item xs={12} sm={12}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  Street address
                </SuiTypography>
              </SuiBox>
              <SuiInput
                placeholder="House number and Street name"
                required
                {...register("address_1", { required: true })}
              />
              <SuiInput
                className="mt-2"
                placeholder="Appartments, suite, unit etc ..."
                {...register("address_2")}
              />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  City
                </SuiTypography>
              </SuiBox>
              <SuiInput placeholder="City" required {...register("city", { required: true })} />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  State/Province
                </SuiTypography>
              </SuiBox>
              {countryCode && countryCode.value == "us" ? (
                <Controller
                  name="province"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <SuiSelect
                      {...field}
                      placeholder="State/Province"
                      isSearchable={true}
                      options={usaStateOptions}
                    />
                  )}
                />
              ) : (
                <SuiInput
                  {...register("province", {
                    required: true,
                  })}
                  placeholder="State/Province"
                  required
                />
              )}
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  Country
                </SuiTypography>
              </SuiBox>
              <Controller
                name="country_code"
                rules={{ required: true }}
                control={control}
                render={({ field }) => (
                  <SuiSelect
                    {...field}
                    placeholder="Country"
                    isSearchable={true}
                    onChange={handleChangeCountry}
                    options={countryOptions}
                  />
                )}
              />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                  required
                >
                  Postcode/ZIP
                </SuiTypography>
              </SuiBox>
              <SuiInput
                placeholder="Postcode/ZIP"
                required
                {...register("postal_code", { required: true })}
              />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                >
                  Company Link
                </SuiTypography>
              </SuiBox>
              <SuiInput placeholder="www.yourcomapany.com" {...register("company_link")} />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                >
                  Instagram Link
                </SuiTypography>
              </SuiBox>
              <SuiInput placeholder="www.instagram.com" {...register("instagram_link")} />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                >
                  Facebook Link
                </SuiTypography>
              </SuiBox>
              <SuiInput placeholder="www.facebook.com" {...register("facebook_link")} />
            </SuiBox>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
              <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                >
                  Twitter Link
                </SuiTypography>
              </SuiBox>
              <SuiInput placeholder="www.twitter.com" {...register("twitter_link")} />
            </SuiBox>
          </Grid>
        </Grid>
        <SuiBox
          pt={3}
          px={3}
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
        >
          {onPrev ? (
            <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
              <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                <SuiButton
                  variant="gradient"
                  color={isPrevDisabled ? "light" : "dark"}
                  sx={{ height: "100%" }}
                  onClick={onPrev}
                  disabled={isPrevDisabled}
                >
                  <ArrowBackIcon fontSize="large" />
                </SuiButton>
              </SuiBox>
            </SuiBox>
          ) : (
            <div />
          )}
          <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
            <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
              <SuiButton variant="gradient" color="dark" sx={{ height: "100%" }} type="submit">
                save
              </SuiButton>
            </SuiBox>
          </SuiBox>
          {onNext ? (
            <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
              <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                <SuiButton
                  variant="gradient"
                  color={isNextDisabled ? "light" : "dark"}
                  sx={{ height: "100%" }}
                  onClick={onNext}
                  disabled={isNextDisabled}
                >
                  <ArrowForwardIcon fontSize="large" />
                </SuiButton>
              </SuiBox>
            </SuiBox>
          ) : null}
        </SuiBox>
      </SuiBox>
    </Card>
  );
};

export default ContactInfo;
